import React, { useEffect, useState } from "react";
import API_URL from "../../services/api";
import "./ManageOrders.css";

const ManageOrders = () => {
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ================= FETCH ORDERS =================

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("token");

      const res = await fetch(`${API_URL}/api/orders`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        throw new Error(`Server returned ${res.status}`);
      }

      const data = await res.json();

      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Orders error:", err);
      setError("Unable to load orders.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // ================= DOWNLOAD CSV =================

  const downloadCSV = () => {
    if (orders.length === 0) {
      alert("There are no orders to download.");
      return;
    }

    const headers = [
      "Order ID",
      "User ID",
      "Customer Name",
      "Email",
      "Total Amount",
      "Payment Method",
      "Phone",
      "Delivery Address",
      "Status",
      "Order Date",
    ];

    const rows = orders.map((order) => [
      order.order_id,
      order.user_id,
      order.name,
      order.email,
      order.total_amount,
      order.payment_method,
      order.phone,
      order.delivery_address,
      order.order_status,
      order.created_at
        ? new Date(order.created_at).toLocaleString()
        : "",
    ]);

    const csvContent = [
      headers.map((header) => `"${header}"`).join(","),
      ...rows.map((row) =>
        row
          .map((value) =>
            `"${String(value ?? "").replace(/"/g, '""')}"`
          )
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");

    link.href = url;
    link.download = `Orders_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  // ================= UPDATE STATUS =================

  const updateStatus = async (orderId, status) => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_URL}/api/orders/${orderId}/status`,
        {
          method: "PUT",

          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },

          body: JSON.stringify({
            status,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to update order.");
      }

      alert(data.message || "Order status updated.");

      fetchOrders();
    } catch (err) {
      console.error("Update status error:", err);

      alert(
        err.message || "Unable to update order status."
      );
    }
  };

  // ================= SEARCH =================

  const searchText = search.toLowerCase().trim();

  const filteredOrders = orders.filter((order) => {
    const name = String(order.name ?? "").toLowerCase();
    const userId = String(order.user_id ?? "").toLowerCase();
    const status = String(
      order.order_status ?? ""
    ).toLowerCase();

    const email = String(
      order.email ?? ""
    ).toLowerCase();

    const orderId = String(
      order.order_id ?? ""
    ).toLowerCase();

    return (
      name.includes(searchText) ||
      userId.includes(searchText) ||
      status.includes(searchText) ||
      email.includes(searchText) ||
      orderId.includes(searchText)
    );
  });

  // ================= STATISTICS =================

  const totalOrders = orders.length;

  const pendingOrders = orders.filter(
    (order) => order.order_status === "Pending"
  ).length;

  const verifiedOrders = orders.filter(
    (order) => order.order_status === "Verified"
  ).length;

  const deliveredOrders = orders.filter(
    (order) => order.order_status === "Delivered"
  ).length;

  const rejectedOrders = orders.filter(
    (order) => order.order_status === "Rejected"
  ).length;

  // ================= STATUS CLASS =================

  const getStatusClass = (status) => {
    switch (status) {
      case "Pending":
        return "status-pending";

      case "Verified":
        return "status-verified";

      case "Delivered":
        return "status-delivered";

      case "Rejected":
        return "status-rejected";

      default:
        return "status-default";
    }
  };

  // ================= LOADING =================

  if (loading) {
    return (
      <div className="orders-page">
        <div className="orders-loading">
          <div className="orders-spinner"></div>
          <p>Loading orders...</p>
        </div>
      </div>
    );
  }

  // ================= ERROR =================

  if (error) {
    return (
      <div className="orders-page">
        <div className="orders-error">
          <div className="orders-error-icon">
            ⚠️
          </div>

          <h2>Unable to Load Orders</h2>

          <p>{error}</p>

          <button
            className="retry-button"
            onClick={fetchOrders}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ================= UI =================

  return (
    <div className="orders-page">

      {/* ================= HEADER ================= */}

      <div className="orders-header">
        <div>
          <h1>Manage Orders</h1>

          <p>
            View, verify and manage customer orders.
          </p>
        </div>
      </div>

      {/* ================= STATISTICS ================= */}

      <div className="orders-stats">

        <div className="order-stat-card total">
          <div className="stat-icon">📦</div>

          <div>
            <h2>{totalOrders}</h2>
            <p>Total Orders</p>
          </div>
        </div>

        <div className="order-stat-card pending">
          <div className="stat-icon">⏳</div>

          <div>
            <h2>{pendingOrders}</h2>
            <p>Pending</p>
          </div>
        </div>

        <div className="order-stat-card verified">
          <div className="stat-icon">✅</div>

          <div>
            <h2>{verifiedOrders}</h2>
            <p>Verified</p>
          </div>
        </div>

        <div className="order-stat-card delivered">
          <div className="stat-icon">🚚</div>

          <div>
            <h2>{deliveredOrders}</h2>
            <p>Delivered</p>
          </div>
        </div>

        <div className="order-stat-card rejected">
          <div className="stat-icon">❌</div>

          <div>
            <h2>{rejectedOrders}</h2>
            <p>Rejected</p>
          </div>
        </div>

      </div>

      {/* ================= TOOLS ================= */}

      <div className="orders-tools">

        <div className="search-wrapper">

          <span className="search-icon">
            🔍
          </span>

          <input
            type="text"
            placeholder="Search order, user, email or status..."
            value={search}
            onChange={(e) =>
              setSearch(e.target.value)
            }
          />

          {search && (
            <button
              className="clear-search"
              onClick={() => setSearch("")}
            >
              ✕
            </button>
          )}

        </div>

        <button
          className="download-button"
          onClick={downloadCSV}
        >
          📥 Download CSV
        </button>

      </div>

      {/* ================= RESULT INFO ================= */}

      <div className="orders-result">

        Showing{" "}
        <strong>{filteredOrders.length}</strong>{" "}
        of{" "}
        <strong>{orders.length}</strong>{" "}
        orders

      </div>

      {/* ================= DESKTOP TABLE ================= */}

      <div className="orders-table-wrapper">

        <table className="orders-table">

          <thead>
            <tr>
              <th>Order ID</th>
              <th>Customer</th>
              <th>Email</th>
              <th>Amount</th>
              <th>Payment</th>
              <th>Phone</th>
              <th>Address</th>
              <th>Status</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>

            {filteredOrders.length === 0 ? (

              <tr>
                <td
                  colSpan="10"
                  className="no-orders"
                >
                  <div>
                    <span>📦</span>

                    <h3>
                      No Orders Found
                    </h3>

                    <p>
                      Try changing your search.
                    </p>
                  </div>
                </td>
              </tr>

            ) : (

              filteredOrders.map((order) => (

                <tr key={order.order_id}>

                  <td>
                    <strong>
                      #{order.order_id}
                    </strong>
                  </td>

                  <td>
                    <strong>
                      {order.name || "N/A"}
                    </strong>
                  </td>

                  <td>
                    {order.email || "N/A"}
                  </td>

                  <td>
                    <strong>
                      Rs. {order.total_amount}
                    </strong>
                  </td>

                  <td>
                    {order.payment_method || "N/A"}
                  </td>

                  <td>
                    {order.phone || "N/A"}
                  </td>

                  <td className="address-cell">
                    {order.delivery_address ||
                      "N/A"}
                  </td>

                  <td>

                    <span
                      className={`order-status ${getStatusClass(
                        order.order_status
                      )}`}
                    >
                      {order.order_status}
                    </span>

                  </td>

                  <td>
                    {order.created_at
                      ? new Date(
                          order.created_at
                        ).toLocaleDateString()
                      : "N/A"}
                  </td>

                  <td className="actions-cell">

                    {order.order_status ===
                      "Pending" && (
                      <div className="action-buttons">

                        <button
                          className="verify-button"
                          onClick={() =>
                            updateStatus(
                              order.order_id,
                              "Verified"
                            )
                          }
                        >
                          ✓ Verify
                        </button>

                        <button
                          className="reject-button"
                          onClick={() =>
                            updateStatus(
                              order.order_id,
                              "Rejected"
                            )
                          }
                        >
                          ✕ Reject
                        </button>

                      </div>
                    )}

                    {order.order_status ===
                      "Verified" && (

                      <button
                        className="deliver-button"
                        onClick={() =>
                          updateStatus(
                            order.order_id,
                            "Delivered"
                          )
                        }
                      >
                        🚚 Delivered
                      </button>

                    )}

                    {order.order_status ===
                      "Rejected" && (

                      <span className="completed-text rejected-text">
                        Order Rejected
                      </span>

                    )}

                    {order.order_status ===
                      "Delivered" && (

                      <span className="completed-text delivered-text">
                        ✓ Delivered
                      </span>

                    )}

                  </td>

                </tr>

              ))

            )}

          </tbody>

        </table>

      </div>

      {/* ================= MOBILE ORDERS ================= */}

      <div className="mobile-orders">

        {filteredOrders.length === 0 ? (

          <div className="mobile-no-orders">
            <span>📦</span>
            <h3>No Orders Found</h3>
            <p>Try changing your search.</p>
          </div>

        ) : (

          filteredOrders.map((order) => (

            <div
              className="mobile-order-card"
              key={order.order_id}
            >

              <div className="mobile-order-header">

                <strong>
                  #{order.order_id}
                </strong>

                <span
                  className={`order-status ${getStatusClass(
                    order.order_status
                  )}`}
                >
                  {order.order_status}
                </span>

              </div>

              <div className="mobile-order-info">

                <div>
                  <span>Customer</span>
                  <strong>
                    {order.name || "N/A"}
                  </strong>
                </div>

                <div>
                  <span>Email</span>
                  <strong>
                    {order.email || "N/A"}
                  </strong>
                </div>

                <div>
                  <span>Amount</span>
                  <strong>
                    Rs. {order.total_amount}
                  </strong>
                </div>

                <div>
                  <span>Payment</span>
                  <strong>
                    {order.payment_method ||
                      "N/A"}
                  </strong>
                </div>

                <div>
                  <span>Phone</span>
                  <strong>
                    {order.phone || "N/A"}
                  </strong>
                </div>

                <div>
                  <span>Date</span>
                  <strong>
                    {order.created_at
                      ? new Date(
                          order.created_at
                        ).toLocaleDateString()
                      : "N/A"}
                  </strong>
                </div>

                <div className="mobile-address">
                  <span>Delivery Address</span>
                  <strong>
                    {order.delivery_address ||
                      "N/A"}
                  </strong>
                </div>

              </div>

              <div className="mobile-actions">

                {order.order_status ===
                  "Pending" && (
                  <>
                    <button
                      className="verify-button"
                      onClick={() =>
                        updateStatus(
                          order.order_id,
                          "Verified"
                        )
                      }
                    >
                      ✓ Verify
                    </button>

                    <button
                      className="reject-button"
                      onClick={() =>
                        updateStatus(
                          order.order_id,
                          "Rejected"
                        )
                      }
                    >
                      ✕ Reject
                    </button>
                  </>
                )}

                {order.order_status ===
                  "Verified" && (

                  <button
                    className="deliver-button"
                    onClick={() =>
                      updateStatus(
                        order.order_id,
                        "Delivered"
                      )
                    }
                  >
                    🚚 Mark Delivered
                  </button>

                )}

                {order.order_status ===
                  "Rejected" && (

                  <span className="completed-text rejected-text">
                    Order Rejected
                  </span>

                )}

                {order.order_status ===
                  "Delivered" && (

                  <span className="completed-text delivered-text">
                    ✓ Order Delivered
                  </span>

                )}

              </div>

            </div>

          ))

        )}

      </div>

    </div>
  );
};

export default ManageOrders;